import { nafColor } from '@nafcore/theme';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { colors } from '@naf-simba/theme';
import { RouteData } from '@appTypes/mapsApiTypes';
import { createAdvancedMarkerElement } from '@components/google-maps/advancedMarkerUtils';
import { decodePolyline, samplePathForAnimation } from '@components/google-maps/polylineUtils';
import { createSafePOIContent } from '@utils/maps/htmlSanitizer';
import { PointOfInterest } from './RouteCharts';

const ANIMATION_DURATION_MS = 2000;
const MAX_ANIMATION_SEGMENTS = 150;

const detachAdvancedMarker = (marker?: google.maps.marker.AdvancedMarkerElement | null) => {
  if (marker) {
    const markerInstance = marker;
    markerInstance.map = null;
  }
};

interface MapV2LogicOptions {
  routeData?: RouteData;
  pointsOfInterest: PointOfInterest[];
  isLoading: boolean;
  animatedPathStroke?: string;
  animate?: boolean;
  mapOptions: google.maps.MapOptions;
}

export const useMapV2Logic = ({
  routeData,
  pointsOfInterest,
  isLoading,
  animatedPathStroke = colors.secondary[500],
  animate,
  mapOptions,
}: MapV2LogicOptions) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [pendingAnimation, setPendingAnimation] = useState(false);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const markerInstancesRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const animationSegmentsRef = useRef<google.maps.Polyline[]>([]);
  const decodedPath = useMemo(() => (routeData ? decodePolyline(routeData.polyline) : null), [routeData]);

  const placeMarkers = useCallback(() => {
    if (!map || pointsOfInterest.length === 0) return;
    markerInstancesRef.current.forEach(detachAdvancedMarker);
    markerInstancesRef.current = pointsOfInterest
      .filter((point) => point.includeOnChart && point.latlng)
      .map((point, index) => {
        const marker = createAdvancedMarkerElement({
          map,
          options: {
            position: point.latlng,
            title: point.title,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: nafColor.signature.white,
              strokeColor: colors.secondary[500],
              fillOpacity: 1,
              strokeWeight: 2,
              scale: 5,
            },
          },
        });
        const infoWindow = new google.maps.InfoWindow({
          content: createSafePOIContent(point.title, point.kmFromStart, point.altitude, index === 0),
        });
        marker.addListener('click', () => {
          infoWindow.open(map);
          infoWindow.setPosition(new google.maps.LatLng(point.latlng.lat, point.latlng.lng));
        });
        return marker;
      });
  }, [map, pointsOfInterest]);

  const clearAnimation = useCallback(() => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    animationSegmentsRef.current.forEach((segment) => segment.setMap(null));
    animationSegmentsRef.current = [];
  }, []);

  const drawFinalRoute = useCallback(() => {
    if (!decodedPath || !map) return;
    const finalPolyline = new google.maps.Polyline({
      path: decodedPath,
      strokeColor: animatedPathStroke,
      strokeOpacity: 1,
      strokeWeight: 4,
      map,
    });
    polylineRef.current?.setMap(null);
    polylineRef.current = finalPolyline;
    placeMarkers();
  }, [animatedPathStroke, decodedPath, map, placeMarkers]);

  const runAnimation = useCallback(() => {
    if (!animate || !decodedPath || !map) return false;
    clearAnimation();
    const sampledPath = samplePathForAnimation(decodedPath, MAX_ANIMATION_SEGMENTS);
    const intervalMs = ANIMATION_DURATION_MS / sampledPath.length;
    let index = 0;
    const id = setInterval(() => {
      if (index + 1 === sampledPath.length) {
        clearAnimation();
        drawFinalRoute();
        return;
      }
      const segment = new google.maps.Polyline({
        path: [sampledPath[index], sampledPath[index + 1]],
        strokeColor: animatedPathStroke,
        map,
        zIndex: index + 2,
      });
      animationSegmentsRef.current.push(segment);
      index += 1;
    }, intervalMs);
    animationIntervalRef.current = id;
    return true;
  }, [animate, decodedPath, map, animatedPathStroke, clearAnimation, drawFinalRoute]);

  useEffect(() => {
    if (ref.current && !map && typeof window !== 'undefined') {
      setMap(
        new window.google.maps.Map(ref.current, {
          ...mapOptions,
          streetViewControl: false,
          mapTypeControl: false,
          mapId: '8df6d3a1ba0dd900',
        }),
      );
    }
    // Map options are intentionally only read while creating the map instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, map]);

  useEffect(() => {
    if (!decodedPath || !map || isLoading) return;
    const bounds = new google.maps.LatLngBounds();
    decodedPath.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
    if (!animate) drawFinalRoute();
  }, [animate, map, decodedPath, isLoading, drawFinalRoute]);

  useEffect(() => {
    if (!pendingAnimation || !map || !decodedPath || isLoading) return;
    if (runAnimation()) setPendingAnimation(false);
  }, [pendingAnimation, map, decodedPath, isLoading, runAnimation]);

  useEffect(
    () => () => {
      polylineRef.current?.setMap(null);
      markerInstancesRef.current.forEach(detachAdvancedMarker);
      clearAnimation();
    },
    [clearAnimation],
  );

  const handleWaypointEnter = () => {
    if (!animate) return;
    if (!map || !decodedPath || isLoading || !runAnimation()) setPendingAnimation(true);
  };

  return { ref, map, handleWaypointEnter };
};
